import axios from 'axios';

import { CONFIG } from 'src/config-global';
import { paths } from 'src/routes/paths';

import { requestReauthentication } from 'src/auth/reauthentication-coordinator';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.site.serverUrl, withCredentials: true });

let antiforgeryRequest;

let sessionRedirectStarted = false;

const mutationMethods = new Set(['post', 'put', 'patch', 'delete']);

const invalidSessionCodes = new Set([
  'bff.session.required',
  'identity.session.missing',
  'identity.session.invalid',
]);

function redirectToSignIn() {
  if (typeof window === 'undefined' || sessionRedirectStarted) {
    return false;
  }

  const { pathname, search, hash } = window.location;

  if (pathname.startsWith('/auth/')) {
    return false;
  }

  sessionRedirectStarted = true;

  const params = new URLSearchParams({
    reason: 'session-expired',
    returnTo: `${pathname}${search}${hash}`,
  });

  window.location.replace(`${paths.auth.jwt.signIn}?${params.toString()}`);
  return true;
}

async function getAntiforgeryToken() {
  if (!antiforgeryRequest) {
    antiforgeryRequest = axiosInstance
      .get('/bff/antiforgery')
      .then((response) => response.data.token)
      .finally(() => {
        antiforgeryRequest = null;
      });
  }

  return antiforgeryRequest;
}

axiosInstance.interceptors.request.use(async (config) => {
  if (mutationMethods.has(config.method?.toLowerCase())) {
    config.headers.set('X-CSRF-TOKEN', await getAntiforgeryToken());
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const problem = error.response?.data;

    if (
      error.response?.status === 401 &&
      invalidSessionCodes.has(problem?.code) &&
      redirectToSignIn()
    ) {
      // The current page will be unloaded. Keeping the request pending prevents
      // page-level handlers from briefly showing a redundant error toast.
      return new Promise(() => {});
    }

    if (
      error.response?.status === 428 &&
      problem?.code === 'identity.reauthentication.required' &&
      !error.config?._reauthenticationRetried &&
      !error.config?.url?.includes('/auth/mfa/reauthenticate')
    ) {
      await requestReauthentication();
      error.config._reauthenticationRetried = true;
      return axiosInstance(error.config);
    }

    if (problem?.title && !problem.message) {
      problem.message = problem.title;
    }
    return Promise.reject(problem || error);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    me: '/bff/user',
    signIn: '/bff/auth/login',
    secondFactor: '/bff/auth/login/second-factor',
    options: '/bff/auth/options',
    captcha: '/bff/auth/captcha',
    passkeyOptions: '/bff/auth/passkey/options',
    passkeyComplete: '/bff/auth/passkey/complete',
    signUp: '/bff/auth/register',
    verify: '/bff/auth/verify',
    resendVerification: '/bff/auth/verify/resend',
    forgotPassword: '/bff/auth/password/forgot',
    resetPassword: '/bff/auth/password/reset',
    signOut: '/bff/logout',
  },
  organizations: '/api/v1/organizations',
  members: '/api/v1/members',
  roles: '/api/v1/roles',
  invitations: '/api/v1/invitations',
  profile: '/api/v1/profile',
  mapLayerPreferences: '/api/v1/profile/map-layer-preferences',
  profileAccessHistory: '/api/v1/profile/access-history',
  permissions: '/api/v1/access/permissions',
  audit: '/api/v1/audit',
  access: '/api/v1/access',
  globalOverview: '/api/v1/access/overview',
  organizationOverview: '/api/v1/organizations/overview',
  securityPolicy: '/api/v1/security-policy',
  mapRuntimeSettings: '/api/v1/security-policy/map-runtime',
  fleet: '/api/v1/fleet',
  geography: '/api/v1/geography',
  mfa: '/api/v1/auth/mfa',
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
};
